/**
 * SignIn — Supabase auth. Google OAuth + magic-link fallback.
 *
 * Visual treatment polished 2026-05-09 per design chat's V1 SignIn polish
 * pass (mockup at /design/mockups/V1_SignIn_Polish_Demo.html, signed off
 * by Russ + brand-discipline-auditor 2026-05-09).
 *
 * Locked grammar applied:
 *   - Wordmark band at top (locked v2 light-BG PNG via the Wordmark component)
 *   - Coral eyebrow "SIGN IN" above the navy H1 (matches Blueprint pages +
 *     Magic Invite email register)
 *   - Google button uses Tier 3 secondaryButtonStyle (2px navy border, white
 *     fill, Inter Bold navy text, multi-color G logo preserved per Google's
 *     brand guidelines, hover-toggled pale-cyan fill)
 *   - "or" divider with chunky hairlines + lowercase Inter "or"
 *   - Email submit uses chunky 2px navy filled button with hover navy-darker
 *   - Magic-link any-email helper line under the submit (Lora italic 13px
 *     text-secondary): "Works with any email — Gmail, Outlook, work, personal."
 *   - Both buttons share min-height 48 for visual balance
 *
 * Auth wiring unchanged from the 2026-05-09 OAuth patch: Google OAuth via
 * supabase.auth.signInWithOAuth as primary; magic-link via signInWithOtp as
 * fallback. Tenant-claim setting happens server-side via signup-finalize.js
 * on first sign-in.
 *
 * Google OAuth requires the Google provider to be enabled in the Supabase
 * Auth dashboard with a Client ID + Secret from a Google Cloud OAuth 2.0
 * client. See /shared/V1_Launch_Readiness_Runbook.md §2 for the setup steps.
 */

import { useState } from "react";
import { supabase } from "../lib/supabase.js";
import { Wordmark } from "../components/index.js";
import {
  secondaryButtonStyle,
  secondaryButtonDisabledStyle,
} from "../lib/buttonStyles.js";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(null);
  // Hover states — React inline styles don't support :hover natively.
  const [googleHover, setGoogleHover] = useState(false);
  const [submitHover, setSubmitHover] = useState(false);

  const signInWithGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin + "/",
        queryParams: { access_type: "offline", prompt: "consent" },
      },
    });
    if (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
    // No setGoogleLoading(false) on success — page redirects to Google's OAuth flow.
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + "/" },
    });
    if (err) {
      setError(err.message);
      setStatus("idle");
      return;
    }
    setStatus("sent");
  };

  return (
    <div
      style={{
        maxWidth: 460,
        margin: "0 auto",
        padding: "64px 24px 80px",
      }}
    >
      {/* Wordmark band — centered, locked v2 light-BG PNG */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 48,
        }}
      >
        <Wordmark scale={1.75} />
      </div>

      {/* Eyebrow — coral Archivo Black, locked v10 grammar */}
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 11,
          lineHeight: 1.4,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-coral)",
          margin: "0 0 10px",
        }}
      >
        Sign in
      </p>

      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 36,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          color: "var(--ppr-color-accent-navy)",
          margin: "0 0 14px",
        }}
      >
        Sign in.
      </h1>

      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontStyle: "italic",
          fontSize: 16,
          lineHeight: 1.55,
          color: "var(--ppr-color-text-secondary)",
          margin: "0 0 32px",
        }}
      >
        Sign in with Google, or get a one-tap link by email.
      </p>

      {status === "sent" ? (
        <div
          style={{
            padding: "16px 18px",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-accent-pale-soft)",
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-base)",
            borderRadius: 6,
          }}
        >
          Check your inbox for a sign-in link sent to <strong>{email}</strong>.
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={googleLoading}
            onMouseEnter={() => setGoogleHover(true)}
            onMouseLeave={() => setGoogleHover(false)}
            aria-label="Sign in with Google"
            style={{
              ...(googleLoading ? secondaryButtonDisabledStyle : secondaryButtonStyle),
              background: googleHover && !googleLoading
                ? "var(--ppr-color-accent-pale-cyan)"
                : "var(--ppr-color-background-surface)",
              marginBottom: 24,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {googleLoading ? "Connecting to Google…" : "Sign in with Google"}
          </button>

          {/* "or" divider — chunky hairlines + lowercase Inter "or" */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              margin: "0 0 24px",
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: 13,
              color: "var(--ppr-color-text-muted)",
            }}
          >
            <div style={{ flex: 1, height: 1, background: "var(--ppr-color-hairline)" }} />
            <span style={{ textTransform: "lowercase", letterSpacing: "0.04em" }}>or</span>
            <div style={{ flex: 1, height: 1, background: "var(--ppr-color-hairline)" }} />
          </div>

          <form onSubmit={submit}>
            <label
              htmlFor="email"
              style={{
                display: "block",
                fontFamily: "var(--ppr-typography-font-family-ui)",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "var(--ppr-color-text-secondary)",
                marginBottom: 8,
              }}
            >
              Work email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              style={{
                width: "100%",
                padding: "13px 14px",
                fontFamily: "var(--ppr-typography-font-family-ui)",
                fontSize: 15,
                border: "1px solid var(--ppr-color-border-default)",
                borderRadius: 6,
                background: "var(--ppr-color-background-surface)",
                color: "var(--ppr-color-text-primary)",
                outline: "none",
                marginBottom: 18,
              }}
            />
            <button
              type="submit"
              disabled={status === "sending"}
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              style={{
                width: "100%",
                minHeight: 48,
                padding: "13px 18px",
                fontFamily: "var(--ppr-typography-font-family-display)",
                fontSize: 14,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                border: "1px solid var(--ppr-color-border-default)",
                background: submitHover && status !== "sending"
                  ? "#0F1B2E"
                  : "var(--ppr-color-accent-navy)",
                color: "var(--ppr-color-text-inverse)",
                borderRadius: 6,
                cursor: status === "sending" ? "wait" : "pointer",
                opacity: status === "sending" ? 0.7 : 1,
                transition: "background 120ms ease",
              }}
            >
              {status === "sending" ? "Sending…" : "Email me a sign-in link"}
            </button>

            {/* Magic-link any-email helper — Lora italic 13px slate, centered
                under the submit button. Removes the "does this work without
                Gmail?" friction surfaced by Russ 2026-05-09. */}
            <p
              style={{
                fontFamily: "var(--ppr-typography-font-family-body)",
                fontStyle: "italic",
                fontSize: 13,
                lineHeight: 1.55,
                color: "var(--ppr-color-text-secondary)",
                margin: "12px 0 0",
                textAlign: "center",
              }}
            >
              Works with any email — Gmail, Outlook, work, personal.
            </p>

            {error ? (
              <div
                style={{
                  marginTop: 12,
                  fontFamily: "var(--ppr-typography-font-family-body)",
                  fontSize: "var(--ppr-typography-font-size-sm)",
                  color: "var(--ppr-color-accent-coral-dark)",
                }}
              >
                {error}
              </div>
            ) : null}
          </form>
        </>
      )}
    </div>
  );
}
